/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMutation } from '@tanstack/react-query';
import { Spin } from 'antd';
import AuthApi from 'apis/AuthApi';
import { IUser } from 'interfaces/User/User';
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react';
import { getSignoutUri } from 'utils/constants/getSignoutUri';
import {
  ACCOUNTANT_ROLE,
  ADMIN_ROLE,
  CN_ROLE,
  HO_ROLE,
  NS_ROLE,
  QLD_ROLE,
  QTCN_ROLE,
  SALE_ROLE,
  STOREKEEPER_ROLE,
  TREASURER_ROLE,
  PC_ROLE,
  KTD_ROLE,
} from 'utils/constants/userRole';

type UserContextValues = {
  user?: IUser & {
    isAdmin: boolean;
    isCN: boolean;
    isQTCN: boolean;
    isSale: boolean;
    isHO: boolean;
    isQLD: boolean;
    isAccountant: boolean;
    isStorekeeper: boolean;
    isTreasurer: boolean;
    isNS: boolean;
    isPC: boolean;
    isKTD: boolean;
  };
  isLoading: boolean;
  getUser: () => void;
  signOut: () => void;
};

const UserContext = createContext<UserContextValues>(undefined as never);

const useUser = (): UserContextValues => useContext(UserContext);

const UserProvider = ({ children }: { children: ReactNode }): JSX.Element => {
  const [user, setUser] = useState<any>();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  // const [user, setUser] = useState<IUser>();

  const handleRemoveToken = () => {
    localStorage.removeItem('token');
    window.location.replace(getSignoutUri());
  };

  const { mutate: getUser } = useMutation({
    mutationFn: AuthApi.getMe,
    onSuccess: (response) => {
      const isAdmin = response.role === ADMIN_ROLE;
      const isQLD = response.role === QLD_ROLE;
      const isQTCN = response.role === QTCN_ROLE;
      const isCN = response.role === CN_ROLE || isQLD || isQTCN;
      const isSale = response.role === SALE_ROLE;
      const isHO = response.role === HO_ROLE;
      const isAccountant = response.role === ACCOUNTANT_ROLE;
      const isStorekeeper = response.role === STOREKEEPER_ROLE;
      const isTreasurer = response.role === TREASURER_ROLE;
      const isNS = response.role === NS_ROLE;
      const isPC = response.role === PC_ROLE;
      const isKTD = response.role === KTD_ROLE;

      setUser({
        ...response,
        isAdmin,
        isCN,
        isQTCN,
        isSale,
        isHO,
        isQLD,
        isAccountant,
        isStorekeeper,
        isTreasurer,
        isNS,
        isPC,
        isKTD,
      });

      setIsLoading(false);
    },
    onError: (error) => {
      handleRemoveToken();
      setIsLoading(false);
    },
  });

  useEffect(() => {
    if (!localStorage.getItem('token')) {
      setIsLoading(false);
      return;
    }
    getUser();
  }, []);

  const signOut = () => {
    handleRemoveToken();
  };

  return (
    <UserContext.Provider value={{ user, getUser, signOut, isLoading }}>
      <Spin className="!max-h-full" size="large" spinning={isLoading}>
        {children}
      </Spin>
    </UserContext.Provider>
  );
};

export { UserProvider, useUser };
